import { useNinetailed } from '@ninetailed/experience.js-next';
import { CreatePetPayload, Pet, UpdatePetPayload, getPets } from 'api/pets';
import { PetType } from 'constants/pet-type';
import {
  AllergySegmentationIds,
  CatNutritionSegmentationIds,
  NutritionSegmentationIds,
  SegmentationIds,
  isAgeSegmentation,
  isBreedSegmentation,
  isNutritionSegmentation,
} from 'constants/segmentation';
import useSyncSegmentation from 'hooks/pet-profile/useSyncSegmentation';
import {
  isFoodType,
  isWetFoodType,
} from 'interfaces/api-input/input-product-type';
import { debounce } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePetProfilesStore } from 'stores/pet-profiles';
import { usePetProfilesApiStore } from 'stores/pet-profiles/api';
import useSWR from 'swr';
import {
  CatProfile,
  DogProfile,
  PetProfile,
  isCatProfile,
  isDogProfile,
} from 'types/pet-profile';
import convertProfileToArray from 'utils/pet-profile/convert-profile-to-array';
import { useAuth } from './auth';
import { usePetContext } from './pet';
import { useSegmentationContext } from './segmentation';
/** Interface for managing multiple pet profiles */
export interface PetProfiles {
  dog: DogProfile | null;
  cat: CatProfile | null;
  activeType: (typeof PetType)[keyof typeof PetType];
}

export interface PetProfileContextType {
  customerPets?: Pet[] | null;
  isProfileEmpty: boolean;
  isProfileMinimal: boolean;
  petProfile: PetProfile;
  deletePetProfile: (petId: string) => Promise<void>;
  setPetProfile: React.Dispatch<React.SetStateAction<PetProfile>>;
  updatePetProfileWithSync: (newProfile: PetProfile) => Promise<void>;
  updatePetProfile: (
    value: SegmentationIds | undefined,
    allergies: AllergySegmentationIds[] | undefined
  ) => void;
  createNewPet: (newPetProfile: PetProfile) => Promise<Pet>;
  switchProfileType: (type: (typeof PetType)[keyof typeof PetType]) => void;
  getCurrentProfileType: () => (typeof PetType)[keyof typeof PetType];
  switchPetById: (id: string) => void;
}

const PetProfileContext = createContext<PetProfileContextType>(
  {} as PetProfileContextType
);

interface PetProfileProviderProps {
  children: React.ReactNode;
}

export const PetProfileProvider: React.FC<PetProfileProviderProps> = ({
  children,
}) => {
  const justSyncedRef = useRef(false);
  const justCreatedPetRef = useRef(false);
  const lastUpdateTimestampRef = useRef(0);
  const { switchContext } = usePetContext();
  const { identify } = useNinetailed();
  const { customer } = useAuth();
  const { setAllPetSegments } = useSegmentationContext();

  const {
    activeType,
    petProfile,
    setPetProfile,
    switchProfileType,
    createNewPetProfile,
    updateDogProfile,
    updateCatProfile,
  } = usePetProfilesStore((state) => ({
    activeType: state.activeType,
    petProfile: state.petProfile,
    setPetProfile: state.setPetProfile,
    switchProfileType: state.switchProfileType,
    createNewPetProfile: state.createNewPetProfile,
    updateDogProfile: state.updateDogProfile,
    updateCatProfile: state.updateCatProfile,
  }));

  const {
    createPet: createPetInApi,
    updatePet: updatePetInApi,
    deletePet: deletePetInApi,
    fetchPets: fetchPetsInApi,
  } = usePetProfilesApiStore((state) => ({
    createPet: state.createPet,
    updatePet: state.updatePet,
    deletePet: state.deletePet,
    fetchPets: state.fetchPets,
  }));

  const { pushSegmentation, multipleSegmentation, removeSegmentation } =
    useSegmentationContext();
  useSyncSegmentation({
    petProfile,
    multipleSegmentation,
    pushSegmentation,
    removeSegmentation,
  });

  const { current } = usePetContext();

  // Use SWR for data fetching
  const {
    data: customerPetsData,
    mutate: revalidatePets,
    isLoading: isLoadingPets,
  } = useSWR(['customerPets', customer?.id], () =>
    customer?.id ? getPets(customer.id) : null
  );

  useEffect(() => {
    if (customer?.id) {
      fetchPetsInApi(customer.id.toString());
    }
  }, [fetchPetsInApi, customer?.id]);

  // Switch profile type when pet context changes
  useEffect(() => {
    if (!current) return;

    const targetType = current === 'dogs' ? PetType.Dog : PetType.Cat;
    if (activeType !== targetType) {
      switchProfileType(targetType);
    }
  }, [current, activeType, switchProfileType]);

  const updateNeedsAndAllergies = (
    prevProfile: PetProfile,
    needs: (NutritionSegmentationIds | CatNutritionSegmentationIds)[],
    value: SegmentationIds,
    allergies: AllergySegmentationIds[] | undefined
  ): PetProfile => {
    // Check if we're removing Intolerance/IntoleranceCat need
    const isRemovingIntoleranceNeed =
      (prevProfile.selectedNeeds?.includes(
        NutritionSegmentationIds.Intolerance
      ) ||
        prevProfile.selectedNeeds?.includes(
          CatNutritionSegmentationIds.IntoleranceCat
        )) &&
      !needs.includes(NutritionSegmentationIds.Intolerance) &&
      !needs.includes(CatNutritionSegmentationIds.IntoleranceCat);

    if (isRemovingIntoleranceNeed) {
      return {
        ...prevProfile,
        selectedNeeds: needs,
        selectedAllergies: [], // Clear allergies when removing Intolerance need
      };
    }

    if (
      !needs.includes(
        value as NutritionSegmentationIds | CatNutritionSegmentationIds
      ) &&
      value !== NutritionSegmentationIds.Intolerance &&
      value !== CatNutritionSegmentationIds.IntoleranceCat
    ) {
      return {
        ...prevProfile,
        selectedNeeds: [
          ...needs,
          value as NutritionSegmentationIds | CatNutritionSegmentationIds,
        ],
      };
    } else if (
      value === NutritionSegmentationIds.Intolerance ||
      value === CatNutritionSegmentationIds.IntoleranceCat ||
      (needs.includes(value as NutritionSegmentationIds) &&
        allergies &&
        allergies.length > 0)
    ) {
      const selectedAllergies = new Set(prevProfile.selectedAllergies ?? []);
      if (allergies) {
        allergies.forEach((allergy) => selectedAllergies.add(allergy));
      }

      return {
        ...prevProfile,
        ...(needs.includes(value as NutritionSegmentationIds)
          ? { selectedNeeds: needs }
          : {
              selectedNeeds: [...needs, value as NutritionSegmentationIds],
            }),
        ...(selectedAllergies.size > 0 && {
          selectedAllergies: Array.from(selectedAllergies),
        }),
      };
    }
    return prevProfile;
  };

  // Debounced API sync function
  const debouncedSyncWithApi = useMemo(
    () =>
      debounce(async (pet: Pet, updatedProfile: PetProfile) => {
        if (!customer?.id || !pet.id) return;

        try {
          const petData: UpdatePetPayload = {
            id: pet.id.toString(),
            customer_id: customer.id.toString(),
            name: updatedProfile.name || '',
            pet_type: updatedProfile.petType,
            age: updatedProfile.selectedAge,
            allergies: updatedProfile.selectedAllergies,
            food_types: updatedProfile.selectedFoodTypes,
            needs: updatedProfile.selectedNeeds,
            ...(updatedProfile.petType === PetType.Dog && {
              breed: (updatedProfile as DogProfile).selectedBreed,
              weight: (updatedProfile as DogProfile).selectedWeight,
            }),
          };

          await updatePetInApi(petData);
          await revalidatePets();
        } catch (error) {
          console.error('Failed to sync pet profile:', error);
        }
      }, 500),
    [customer?.id, updatePetInApi, revalidatePets]
  );

  /**
   * Update the pet profile with a new value based on the segmentation id
   */
  const updatePetProfile = useCallback(
    (
      value: SegmentationIds | undefined,
      allergies: AllergySegmentationIds[] | undefined
    ): void => {
      if (!value) return;

      setPetProfile((prevProfile) => {
        const needs = prevProfile.selectedNeeds ?? [];
        let updatedProfile = prevProfile;

        if (isAgeSegmentation(value)) {
          updatedProfile = { ...prevProfile, selectedAge: value };
        } else if (
          isBreedSegmentation(value) &&
          prevProfile.petType === PetType.Dog
        ) {
          updatedProfile = {
            ...prevProfile,
            selectedBreed: value,
          } as DogProfile;
        } else if (isNutritionSegmentation(value)) {
          updatedProfile = updateNeedsAndAllergies(
            prevProfile,
            needs,
            value,
            allergies
          );
        }

        // Sync with API if profile has changed and we have a matching pet
        if (updatedProfile !== prevProfile && customer?.id) {
          const currentPet = customerPetsData?.find(
            (p) =>
              p.name === updatedProfile.name &&
              p.pet_type === updatedProfile.petType
          );

          if (currentPet) {
            debouncedSyncWithApi(currentPet, updatedProfile);
          }
        }

        return updatedProfile;
      });
    },
    [customer?.id, customerPetsData, debouncedSyncWithApi, setPetProfile]
  );

  // Function to get current profile type
  const getCurrentProfileType = (): (typeof PetType)[keyof typeof PetType] => {
    return activeType;
  };

  // Check if the user has a 0 or 1 attribute in their pet profile
  const isProfileMinimal = useMemo(() => {
    return (
      convertProfileToArray({ petProfile }).filter(
        (v) => !isFoodType(v) && !isWetFoodType(v)
      ).length <= 1
    );
  }, [petProfile]);

  // Check if the user has no attribute in their pet profile
  const isProfileEmpty = useMemo(() => {
    return (
      convertProfileToArray({ petProfile }).filter(
        (v) => !isFoodType(v) && !isWetFoodType(v)
      ).length === 0
    );
  }, [petProfile]);

  /**
   * Gets the target pet from an array of pets based on the following priority:
   * 1. If there's a pet with matching ID as the current profile, return that pet
   * 2. If there's a pet with null name (indicating an incomplete profile), return that pet
   * 3. Otherwise, return the most recently updated pet
   */
  const getTargetPet = useCallback(
    (pets: Pet[]): Pet => {
      // First priority: Find a pet with matching ID
      if (petProfile.id) {
        const matchingIdPet = pets.find((pet) => pet.id === petProfile.id);
        if (matchingIdPet) return matchingIdPet;
      }

      // Second priority: Find a pet with null name (incomplete profile)
      const nullNamePet = pets.find((pet) => pet.name === null);
      if (nullNamePet) return nullNamePet;

      // Third priority: Get the most recently updated pet
      return pets.reduce((prev, curr) =>
        new Date(curr.updated_at).getTime() >
        new Date(prev.updated_at).getTime()
          ? curr
          : prev
      );
    },
    [petProfile]
  );

  /**
   * Helper function to sync pet profile array with Ninetailed
   */
  const syncPetProfileWithNinetailed = useCallback(
    (profile: PetProfile) => {
      const petProfileArray = convertProfileToArray({ petProfile: profile });
      identify(`${customer ? customer.customerId : ''}`, {
        petProfile: petProfileArray,
      });
    },
    [customer, identify]
  );

  /**
   * Helper function to sync all customer pets with Ninetailed
   */
  const getPetsArrays = useCallback((pets: Pet[]) => {
    if (!pets.length) return [];

    const allPetsProfiles = pets.map(
      (pet) =>
        ({
          id: pet.id,
          name: pet.name || '',
          selectedAge: pet.age ?? undefined,
          selectedAllergies: pet.allergies,
          selectedBreed: pet.breed ?? undefined,
          selectedFoodTypes: pet.food_types,
          selectedNeeds: pet.needs,
          selectedWeight: pet.weight,
          updatedAt: pet.updated_at,
          petType: PetType.Dog,
        }) as DogProfile
    );

    // Convert all pets to profile arrays and combine them
    const allPetsProfileArrays = allPetsProfiles
      .map((profile) => convertProfileToArray({ petProfile: profile }))
      .flat()
      .filter((segment): segment is SegmentationIds =>
        Object.values(SegmentationIds).includes(segment as SegmentationIds)
      );

    // Identify with all pets data
    return allPetsProfileArrays;
  }, []);

  /**
   * Helper function to sync all customer pets with Ninetailed
   */
  const syncAllPetsWithNinetailed = useCallback(
    (allPetsProfileArray: string[]) => {
      // Identify with all pets data
      identify(`${customer ? customer.customerId : ''}`, {
        petProfile: allPetsProfileArray,
      });
    },
    [customer, identify]
  );

  /**
   * Effect to sync all pets when they are fetched
   */
  useEffect(() => {
    if (customerPetsData && customerPetsData.length > 0) {
      const allPetsProfileArrays = getPetsArrays(customerPetsData);
      syncAllPetsWithNinetailed(allPetsProfileArrays);
      setAllPetSegments(allPetsProfileArrays);
    }
  }, [
    customerPetsData,
    syncAllPetsWithNinetailed,
    setAllPetSegments,
    getPetsArrays,
  ]);

  const initRef = useRef(false);

  /**
   * Sync pet profile with the one from the server
   */
  useEffect(() => {
    if (initRef.current || isLoadingPets || !customerPetsData) {
      return;
    }

    if (customerPetsData.length === 0) return;

    initRef.current = true;

    // Group pets by type
    const dogPets = customerPetsData.filter(
      (pet) => pet.pet_type === PetType.Dog
    );
    const catPets = customerPetsData.filter(
      (pet) => pet.pet_type === PetType.Cat
    );

    // Process dog profiles if any exist
    if (dogPets.length > 0) {
      const dogPet = dogPets[0]; // Take the first dog profile
      const dogProfile: DogProfile = {
        id: dogPet.id,
        name: dogPet.name || '',
        petType: PetType.Dog,
        selectedAge: dogPet.age ?? undefined,
        selectedAllergies: dogPet.allergies || [],
        selectedFoodTypes: dogPet.food_types || [],
        selectedNeeds: dogPet.needs || [],
        updatedAt: dogPet.updated_at,
        selectedBreed: dogPet.breed ?? undefined,
        selectedWeight: dogPet.weight,
      };
      updateDogProfile(dogProfile);
      syncPetProfileWithNinetailed(dogProfile);
    }

    // Process cat profiles if any exist
    if (catPets.length > 0) {
      const catPet = catPets[0]; // Take the first cat profile
      const catProfile: CatProfile = {
        id: catPet.id,
        name: catPet.name || '',
        petType: PetType.Cat,
        selectedAge: catPet.age ?? undefined,
        selectedAllergies: catPet.allergies || [],
        selectedFoodTypes: catPet.food_types || [],
        selectedNeeds: catPet.needs || [],
        updatedAt: catPet.updated_at,
        selectedWetFoodTypes: catPet.wet_food_tags || [],
        selectedTastePreferences: catPet.protein_tags || [],
        selectedWeight: catPet.weight,
      };
      updateCatProfile(catProfile);
      syncPetProfileWithNinetailed(catProfile);
    }
  }, [
    customerPetsData,
    syncPetProfileWithNinetailed,
    updateDogProfile,
    updateCatProfile,
    isLoadingPets,
  ]);

  const [isCreatingNewPet, setIsCreatingNewPet] = useState(false);

  /**
   * Creates a new pet profile and updates the state
   * @param newPetProfile - The new pet profile data to create
   * @returns Promise that resolves when the pet is created and state is updated
   */
  const createNewPet = useCallback(
    async (newPetProfile: PetProfile) => {
      const buildPetData = (
        profile: PetProfile,
        customerId?: string
      ): UpdatePetPayload => {
        const basePayload = {
          name: profile.name || '',
          pet_type: profile.petType,
          age: profile.selectedAge,
          needs: profile.selectedNeeds || [],
          allergies: profile.selectedAllergies || [],
          food_types: profile.selectedFoodTypes || [],
          weight: profile.selectedWeight,
        };

        if (isDogProfile(profile)) {
          return {
            ...basePayload,
            breed: profile.selectedBreed,
            ...(customerId && { customer_id: customerId }),
          };
        } else if (isCatProfile(profile)) {
          return {
            ...basePayload,
            wet_food_tags: profile.selectedWetFoodTypes || [],
            protein_tags: profile.selectedTastePreferences || [],
            ...(customerId && { customer_id: customerId }),
          };
        }

        throw new Error('Invalid profile type');
      };

      const updateLocal = (profile: PetProfile): void => {
        setPetProfile(profile);
        syncPetProfileWithNinetailed(profile);
      };

      if (!customer?.id) {
        updateLocal(newPetProfile);
        return {
          name: newPetProfile.name || '',
          age: newPetProfile.selectedAge,
          needs: newPetProfile.selectedNeeds || [],
          allergies: newPetProfile.selectedAllergies || [],
          food_types: newPetProfile.selectedFoodTypes || [],
          pet_type: newPetProfile.petType,
          weight: newPetProfile.selectedWeight,
          ...(isDogProfile(newPetProfile) && {
            breed: newPetProfile.selectedBreed,
          }),
          ...(isCatProfile(newPetProfile) && {
            wet_food_types: newPetProfile.selectedWetFoodTypes || [],
            taste_preferences: newPetProfile.selectedTastePreferences || [],
          }),
          updated_at: new Date().toISOString(),
        };
      }

      try {
        setIsCreatingNewPet(true);
        const petData = buildPetData(newPetProfile, customer.id.toString());
        const createdPet = await createPetInApi(petData);

        // switch context to the pet type
        switchContext(createdPet.pet_type === PetType.Dog ? 'dogs' : 'cats');

        const updatedProfile: PetProfile = {
          ...newPetProfile,
          id: createdPet.id,
          updatedAt: createdPet.updated_at,
        };

        updateLocal(updatedProfile);
        await revalidatePets();
        return createdPet;
      } catch (error) {
        console.error('Failed to create new pet:', error);
        throw error;
      } finally {
        setIsCreatingNewPet(false);
      }
    },
    [
      customer?.id,
      revalidatePets,
      setPetProfile,
      syncPetProfileWithNinetailed,
      createPetInApi,
      switchContext,
    ]
  );

  /**
   * Handle updates to server
   */
  useEffect(() => {
    if (
      !customer?.id ||
      !customerPetsData ||
      isCreatingNewPet ||
      justSyncedRef.current ||
      justCreatedPetRef.current
    ) {
      if (justSyncedRef.current) justSyncedRef.current = false;
      if (justCreatedPetRef.current) justCreatedPetRef.current = false;
      return;
    }

    const now = Date.now();
    if (now - lastUpdateTimestampRef.current < 1000) return;

    if (petProfile.id) {
      const targetPet = customerPetsData.find(
        (pet) => pet.id === petProfile.id
      );
      if (targetPet) {
        const basePayload = {
          id: targetPet.id,
          pet_type: petProfile.petType,
          name: petProfile.name || '',
          age: petProfile.selectedAge ?? null,
          needs: petProfile.selectedNeeds,
          allergies: petProfile.selectedAllergies,
          food_types: petProfile.selectedFoodTypes,
        };

        const currentPet: UpdatePetPayload = isDogProfile(petProfile)
          ? {
              ...basePayload,
              breed: petProfile.selectedBreed ?? null,
              weight: petProfile.selectedWeight,
            }
          : {
              ...basePayload,
              wet_food_tags:
                (petProfile as CatProfile).selectedWetFoodTypes ?? [],
              protein_tags:
                (petProfile as CatProfile).selectedTastePreferences ?? [],
            };

        lastUpdateTimestampRef.current = Date.now();
        updatePetInApi(currentPet)
          .then(() => revalidatePets())
          .catch(console.error);
        return;
      }
    }

    if (customerPetsData.length === 0 && customer?.id) {
      const hasProfileData =
        petProfile.selectedAge !== undefined ||
        (isDogProfile(petProfile) && petProfile.selectedBreed !== undefined) ||
        (isCatProfile(petProfile) &&
          (petProfile.selectedWetFoodTypes?.length > 0 ||
            petProfile.selectedTastePreferences?.length > 0)) ||
        (petProfile.selectedNeeds && petProfile.selectedNeeds.length > 0);

      if (!hasProfileData) return;

      const basePayload = {
        customer_id: customer.id.toString(),
        pet_type: petProfile.petType,
        name: petProfile.name || '',
        age: petProfile.selectedAge ?? null,
        needs: petProfile.selectedNeeds,
        allergies: petProfile.selectedAllergies,
        food_types: petProfile.selectedFoodTypes,
      };

      const petData: CreatePetPayload = isDogProfile(petProfile)
        ? {
            ...basePayload,
            breed: petProfile.selectedBreed ?? null,
            weight: petProfile.selectedWeight,
          }
        : {
            ...basePayload,
            wet_food_tags:
              (petProfile as CatProfile).selectedWetFoodTypes ?? [],
            protein_tags:
              (petProfile as CatProfile).selectedTastePreferences ?? [],
          };

      createPetInApi(petData)
        .then((createdPet) => {
          justCreatedPetRef.current = true;
          setPetProfile((prev) => ({
            ...prev,
            id: createdPet.id,
            updatedAt: createdPet.updated_at,
          }));
          lastUpdateTimestampRef.current = Date.now();
          return revalidatePets();
        })
        .catch(console.error);
    }
  }, [
    customer?.id,
    customerPetsData,
    petProfile,
    isCreatingNewPet,
    updatePetInApi,
    createPetInApi,
    setPetProfile,
    revalidatePets,
  ]);

  const deletePetProfile = useCallback(
    async (petId: string) => {
      try {
        await deletePetInApi(petId);

        const remainingPets =
          customerPetsData?.filter((pet) => pet.id !== petId) ?? [];

        const deletedPet = customerPetsData?.find((pet) => pet.id === petId);

        const remainingPetsOfSameType = remainingPets.filter(
          (pet) => pet.pet_type === deletedPet?.pet_type
        );

        if (remainingPetsOfSameType.length === 0) {
          createNewPetProfile(
            deletedPet?.pet_type as (typeof PetType)[keyof typeof PetType]
          );
        }

        if (remainingPets.length > 0) {
          const nextPet = getTargetPet(remainingPets);
          const basePayload = {
            id: nextPet.id,
            name: nextPet.name || '',
            selectedAge: nextPet.age ?? undefined,
            selectedAllergies: nextPet.allergies ?? [],
            selectedFoodTypes: nextPet.food_types ?? [],
            selectedNeeds: nextPet.needs ?? [],
            petType: nextPet.pet_type,
          };

          const petPayload: PetProfile =
            nextPet.pet_type === PetType.Dog
              ? ({
                  ...basePayload,
                  selectedBreed: nextPet.breed ?? undefined,
                  selectedWeight: nextPet.weight,
                } as DogProfile)
              : ({
                  ...basePayload,
                  selectedWetFoodTypes: nextPet.wet_food_tags ?? [],
                  selectedTastePreferences: nextPet.protein_tags ?? [],
                } as CatProfile);
          // switch context to the pet type
          switchContext(nextPet.pet_type === PetType.Dog ? 'dogs' : 'cats');
          setPetProfile(petPayload);
        } else {
          createNewPetProfile(activeType);
        }

        await revalidatePets();
      } catch (error) {
        console.error('Failed to delete pet:', error);
        throw error;
      }
    },
    [
      deletePetInApi,
      customerPetsData,
      revalidatePets,
      getTargetPet,
      switchContext,
      setPetProfile,
      createNewPetProfile,
      activeType,
    ]
  );

  /**
   * Updates the pet profile and syncs with the backend
   * @param newProfile - The new pet profile to set
   * @returns Promise that resolves when the backend sync is complete
   */
  const updatePetProfileWithSync = useCallback(
    async (newProfile: PetProfile) => {
      if (!customer?.id || !newProfile.id) {
        throw new Error('Cannot update profile: missing customer ID or pet ID');
      }

      // Check if intolerance needs are removed and clear allergies if needed
      const shouldClearAllergies =
        !newProfile.selectedNeeds?.includes(
          NutritionSegmentationIds.Intolerance
        ) &&
        !newProfile.selectedNeeds?.includes(
          CatNutritionSegmentationIds.IntoleranceCat
        );

      const validatedProfile = {
        ...newProfile,
        selectedAllergies: shouldClearAllergies
          ? []
          : newProfile.selectedAllergies,
      };

      const basePayload = {
        id: validatedProfile.id,
        pet_type: validatedProfile.petType,
        name: validatedProfile.name || '',
        age: validatedProfile.selectedAge ?? null,
        needs: validatedProfile.selectedNeeds,
        allergies: validatedProfile.selectedAllergies,
        food_types: validatedProfile.selectedFoodTypes,
        weight: validatedProfile.selectedWeight,
      };

      const currentPet: UpdatePetPayload = isDogProfile(validatedProfile)
        ? {
            ...basePayload,
            breed: validatedProfile.selectedBreed ?? null,
          }
        : {
            ...basePayload,
            wet_food_tags: validatedProfile.selectedWetFoodTypes ?? [],
            protein_tags: validatedProfile.selectedTastePreferences ?? [],
          };

      try {
        await updatePetInApi(currentPet);
        setPetProfile(validatedProfile);
        syncPetProfileWithNinetailed(validatedProfile);
        await revalidatePets();
      } catch (error) {
        console.error('Failed to update pet profile:', error);
        throw error;
      }
    },
    [
      customer?.id,
      revalidatePets,
      setPetProfile,
      syncPetProfileWithNinetailed,
      updatePetInApi,
    ]
  );

  /**
   * Switches to a pet profile by its ID
   * @param id - The ID of the pet to switch to
   */
  const switchPetById = useCallback(
    (id: string) => {
      if (!customerPetsData) return;

      const targetPet = customerPetsData.find((pet) => pet.id === id);
      if (!targetPet) return;

      const basePayload = {
        id: targetPet.id,
        name: targetPet.name || '',
        selectedAge: targetPet.age ?? undefined,
        selectedAllergies: targetPet.allergies ?? [],
        selectedFoodTypes: targetPet.food_types ?? [],
        selectedNeeds: targetPet.needs ?? [],
        petType: targetPet.pet_type,
        updatedAt: targetPet.updated_at,
        selectedWeight: targetPet.weight,
      };

      const petPayload: PetProfile =
        targetPet.pet_type === PetType.Dog
          ? ({
              ...basePayload,
              selectedBreed: targetPet.breed ?? undefined,
            } as DogProfile)
          : ({
              ...basePayload,
              selectedWetFoodTypes: targetPet.wet_food_tags ?? [],
              selectedTastePreferences: targetPet.protein_tags ?? [],
            } as CatProfile);

      // Switch context to the pet type
      switchContext(targetPet.pet_type === PetType.Dog ? 'dogs' : 'cats');
      setPetProfile(petPayload);
      syncPetProfileWithNinetailed(petPayload);
    },
    [
      customerPetsData,
      switchContext,
      setPetProfile,
      syncPetProfileWithNinetailed,
    ]
  );

  const value = {
    customerPets: customerPetsData,
    isProfileEmpty,
    isProfileMinimal,
    petProfile,
    deletePetProfile,
    setPetProfile,
    updatePetProfile,
    updatePetProfileWithSync,
    createNewPet,
    switchProfileType,
    getCurrentProfileType,
    switchPetById,
  };

  return (
    <PetProfileContext.Provider value={value}>
      {children}
    </PetProfileContext.Provider>
  );
};

export const usePetProfile = (): PetProfileContextType =>
  useContext(PetProfileContext);
